import React, { useMemo } from 'react';

const ReactMarkdown = require('react-markdown');
const getHtmlParserPlugin = require('react-markdown/lib/plugins/html-parser');
const HtmlToReact = require('html-to-react');

import gfm from 'remark-gfm';
import { AnnotationAnchor } from './annotation-anchor';
import { AnnotationMark, type AnnotationMarkProps } from './annotation-mark';
import {
  ANCHOR_NODE,
  annotationPlugin,
  coveringRanges,
  MARK_NODE,
  toRanges
} from './annotation-mdast-plugin';
import type { ViewAnnotation } from './cmx-document-content-adapter';
import { MEDIA_TYPE_PLAIN } from './cmx-document-content-types';
import './styles/index.scss';

// Some bodies contain raw HTML of their own -- converters emit <table>/<td>/<br />
// for tables they will not express in pipe syntax (measured: 27 of 162 corpus
// bodies). react-markdown@5.0.3 predates rehype-raw, so its raw-HTML story is
// `astPlugins` + this html-parser factory backed by html-to-react. Bare
// `react-markdown` rather than `react-markdown/with-html`, which hardcodes an
// unconfigurable parser.
//
// NOTE this is no longer load-bearing for ANNOTATIONS -- those travel through
// the mdast tree (see annotation-mdast-plugin.ts) instead of being spliced into
// the Markdown as <mark> tags. That decoupling is the real win: the highlight
// mechanism no longer requires trusting HTML in the document.
const processNodeDefinitions = new HtmlToReact.ProcessNodeDefinitions(React);
const htmlParser = getHtmlParserPlugin({
  processingInstructions: [
    {
      shouldProcessNode: () => true,
      processNode: processNodeDefinitions.processDefaultNode
    }
  ]
});

const NO_ANNOTATIONS: ViewAnnotation[] = [];

export type PlainTextFragment = {
  start: number;
  end: number;
  text: string;
  annotationIds: string[];
  annotationKinds: string[];
};

/**
 * Splits a plain text body at every annotation boundary.
 *
 * Plain text has no markup, so a fragment's offsets are its offsets in the
 * body directly -- none of the mdast offset mapping the Markdown path needs.
 * Offsets are string indices, the same unit remark's positions are in, so a
 * span reads the same over either renderer.
 */
export function plainTextFragments(
  text: string,
  annotations: ViewAnnotation[]
): PlainTextFragment[] {
  const ranges = toRanges(annotations);
  const boundaries = new Set<number>([0, text.length]);
  for (const range of ranges) {
    if (range.start > 0 && range.start < text.length) {
      boundaries.add(range.start);
    }
    if (range.end > 0 && range.end < text.length) {
      boundaries.add(range.end);
    }
  }
  const sorted = Array.from(boundaries).sort((a, b) => a - b);

  const fragments: PlainTextFragment[] = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const start = sorted[i];
    const end = sorted[i + 1];
    const covering = coveringRanges(ranges, start, end);
    fragments.push({
      start,
      end,
      text: text.slice(start, end),
      annotationIds: covering.map((range) => range.id),
      annotationKinds: Array.from(new Set(covering.map((range) => range.kind)))
    });
  }
  return fragments;
}

export type CmxDocumentContentBodyProps = {
  /**
   * The body. `body.text` is optional in the contract (absent for a
   * `?body=reference` read), so callers may legitimately hold nothing.
   */
  text: string | null | undefined;
  /**
   * The body's media type (`body.mediaType`). `text/plain` renders the text
   * exactly as written; anything else, or nothing, renders it as Markdown.
   */
  mediaType?: string;
  /** Annotations to highlight. Empty for any layer 1-3 read. */
  annotations?: ViewAnnotation[];
  /** Rendered when there is no body text to show. */
  emptyLabel?: string;
  /**
   * Wrap every text fragment with its source range, so a selection can be
   * resolved to a Markdown offset. Needed only when the body is annotatable;
   * it adds a wrapper element per text node. See ANCHOR_NODE.
   */
  anchors?: boolean;
  /**
   * Makes marks whose kind is in `activatableKinds` focusable and operable,
   * calling this with every id covering the activated fragment. Used to remove
   * a coder's own span; absent for a display-only body.
   */
  onAnnotationActivate?: (annotationIds: string[]) => void;
  /** Kinds `onAnnotationActivate` applies to. */
  activatableKinds?: string[];
  /** Accessible label for an activatable mark. */
  activateLabel?: string;
};

/**
 * Renders a CMX Document Content body with a layer's annotations highlighted
 * inline. This is the CDMX-20059 view: the body with no sections.
 *
 * A `text/plain` body (CDMX-20397) is shown verbatim, never parsed as Markdown:
 * a plain note's line breaks, indents, `#`, `*` and dashed lines are the
 * author's characters, and a Markdown parser would join, indent-code and
 * headline them. Highlights and anchors use the same AnnotationMark and
 * AnnotationAnchor elements, so selection and span removal work unchanged.
 *
 * Annotations are attached to the parsed tree, never to the source string. The
 * source reaching remark is byte-for-byte the body as served, so every Markdown
 * construct parses exactly as it would unannotated -- which is what makes
 * headings, tables and emphasis all render correctly at once. See
 * annotation-mdast-plugin.ts for why the string-splicing approach could not.
 */
export const CmxDocumentContentBody: React.FC<CmxDocumentContentBodyProps> = ({
  text,
  mediaType,
  annotations = NO_ANNOTATIONS,
  emptyLabel = 'No document content.',
  anchors = false,
  onAnnotationActivate,
  activatableKinds,
  activateLabel = 'Remove this span'
}) => {
  // Only marks of an activatable kind become controls. A producer's section is
  // not the coder's to remove, so making every mark focusable would add dozens
  // of tab stops that do nothing.
  const markRenderer = useMemo(() => {
    if (!onAnnotationActivate) {
      return AnnotationMark;
    }

    const kinds = activatableKinds ?? [];

    return function ActivatableMark(props: AnnotationMarkProps) {
      const activatable = (props.annotationKinds ?? []).some((kind) =>
        kinds.includes(kind)
      );

      return (
        <AnnotationMark
          {...props}
          onActivate={activatable ? onAnnotationActivate : undefined}
          activateLabel={activateLabel}
        />
      );
    };
  }, [onAnnotationActivate, activatableKinds, activateLabel]);

  // Memoize the ELEMENT, not just the plugin list. react-markdown@5 parses the
  // source and runs every transform inside its function body on each render
  // with no memoization of its own, so an unrelated re-render of the host
  // re-parses the whole body and re-walks every text node. Measured on the
  // largest corpus body: ~27ms with a section layer, but ~193ms once the
  // annotation count reaches a few thousand, which a finer-grained layer will.
  const plain = mediaType === MEDIA_TYPE_PLAIN;

  const document = useMemo(() => {
    if (!text) {
      return null;
    }

    if (plain) {
      const Mark = markRenderer;
      return plainTextFragments(text, annotations).map((fragment) => {
        const key = `${fragment.start}-${fragment.end}`;
        if (fragment.annotationIds.length > 0) {
          return (
            <Mark
              key={key}
              annotationIds={fragment.annotationIds}
              annotationKinds={fragment.annotationKinds}
              srcStart={fragment.start}
              srcEnd={fragment.end}
            >
              {fragment.text}
            </Mark>
          );
        }
        return anchors ? (
          <AnnotationAnchor
            key={key}
            srcStart={fragment.start}
            srcEnd={fragment.end}
          >
            {fragment.text}
          </AnnotationAnchor>
        ) : (
          <React.Fragment key={key}>{fragment.text}</React.Fragment>
        );
      });
    }

    return (
      /*
        `gfm` is required, not optional polish: react-markdown@5 sits on
        remark-parse@9, which is strict CommonMark -- pipe tables, strikethrough
        and autolinks all parse as literal text without the GFM extension. The
        converted notes are table-heavy (Epic emits note metadata and whole
        section bodies as tables), so without this the bodies render as walls of
        `|` characters.
      */
      <ReactMarkdown
        source={text}
        allowDangerousHtml
        plugins={[
          gfm,
          [annotationPlugin, { annotations, source: text, anchors }]
        ]}
        astPlugins={[htmlParser]}
        renderers={{
          [MARK_NODE]: markRenderer,
          [ANCHOR_NODE]: AnnotationAnchor
        }}
      />
    );
  }, [text, plain, annotations, anchors, markRenderer]);

  return (
    <div
      className={
        plain
          ? 'cmx-document-content-body cmx-document-content-body--plain'
          : 'cmx-document-content-body'
      }
    >
      {document ?? (
        <div className="cmx-document-content-body__empty">{emptyLabel}</div>
      )}
    </div>
  );
};
